import { lazy, Suspense, useEffect, useId, useRef, useState } from "react";
import { Kbd, cn } from "@magicweave/design";

import { DJINN_NAME, DJINN_SHORTCUT } from "../copy/djinn";
import { clock } from "../djinn/format";
import { lampRead, type LampMood } from "../djinn/lamp-state";
import { useDjinnLampLedger } from "../djinn/ledger-hooks";
import { LampMark, type LampLook } from "../djinn/lamp-mark";
import { useIsPhone } from "../djinn/phone";

/** The peek and the phone's one-time word load on first use: the lamp is in every page's first paint, they are not. */
const Peek = lazy(() => import("./djinn-lamp-more").then((m) => ({ default: m.Peek })));
const PhoneFinding = lazy(() =>
  import("./djinn-lamp-more").then((m) => ({ default: m.PhoneFinding })),
);

/** The moods where Djinn has something for you: the lamp glows and a count says how many. */
const HAS = new Set<LampMood>(["waiting", "found", "several"]);

/** What the mark shows: still while the dock is open (the genie is out), else what the ledger says. */
export function lampLook(mood: LampMood, sheetOpen: boolean): LampLook {
  if (sheetOpen) return "out";
  if (mood === "working") return "working";
  if (mood === "down") return "paused";
  return HAS.has(mood) ? "has" : "idle";
}

/** The time since the page read the ledger, counted on each second while a fix runs. */
function useSecond(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const tick = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(tick);
    };
  }, [active]);
  return now;
}

/**
 * What the lamp shows beside its name, in words: the newest finding, "+1 more", a running fix's
 * clock, how many wait on you, or that Djinn cannot think. A calm lamp says nothing.
 */
export function lampWords(
  mood: LampMood,
  say: string | null,
  clockText: string | null,
): string | null {
  if (mood === "working") return `${say ?? ""} · ${clockText ?? "0:00"}`;
  return say;
}

/**
 * Djinn's lamp in the top bar: always the mark, the name and the shortcut, so the bar never moves.
 * Its state is a dot on the mark (amber: news or waiting on you; grey: can't think) and a turning
 * wisp while a fix runs; the words are on hover. Pressing it, or Cmd/Ctrl+J, opens the sheet.
 * Resting on a finding for 400 ms, or focusing it, shows a peek at it.
 */
export function DjinnLamp({
  projectId,
  onOpen,
  sheetOpen = false,
}: {
  projectId: string;
  onOpen: () => void;
  /** The sheet is open, so the lamp reads a little faster. */
  sheetOpen?: boolean;
}) {
  const fetched = useDjinnLampLedger(projectId, { sheetOpen });
  const ledger = fetched.data;
  const readAt = fetched.dataUpdatedAt;
  const read = lampRead(ledger, Date.now());
  const now = useSecond(read.mood === "working");
  const elapsed =
    read.elapsedS * 1000 + (read.mood === "working" ? Math.max(0, now - readAt) : 0);
  const words = lampWords(read.mood, read.say, clock(0, elapsed));
  // The count is the For-you list's; a finding the watch raised counts when nothing else waits.
  const count = HAS.has(read.mood) ? read.forYou.length || read.findings.length : 0;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "j") {
        event.preventDefault();
        onOpen();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [onOpen]);

  const [peeking, setPeeking] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const peekId = useId();
  const rest = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setPeeking(true);
    }, 400);
  };
  const leave = () => {
    if (timer.current) clearTimeout(timer.current);
    setPeeking(false);
  };
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  // The peek goes on Escape, whether it came from hover or from focus.
  useEffect(() => {
    if (!peeking) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPeeking(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [peeking]);
  const phone = useIsPhone();
  const found = read.mood === "found" || read.mood === "several";
  const newest = read.findings[0];
  const down = read.mood === "down";
  const downWord = (read.say ?? "").replace(/^is /, "").replace(/ right now$/, "");
  const name = words ? `${DJINN_NAME}, ${words}` : DJINN_NAME;
  const label = !words && !sheetOpen ? `Summon ${DJINN_NAME}` : name;

  return (
    // The wrapper only closes the peek on Escape; the button is the control.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="relative"
      onMouseEnter={found && !sheetOpen ? rest : undefined}
      onMouseLeave={leave}
      onFocus={found && !sheetOpen ? rest : undefined}
      onBlur={leave}
      onKeyDown={(event) => {
        if (event.key === "Escape" && peeking) setPeeking(false);
      }}
    >
      <button
        type="button"
        data-djinn="lamp"
        data-state={read.mood}
        aria-label={label}
        aria-keyshortcuts="Meta+J Control+J"
        aria-describedby={peeking ? peekId : undefined}
        onClick={() => {
          leave();
          onOpen();
        }}
        title={found || !words ? undefined : words}
        aria-pressed={sheetOpen}
        className={cn(
          "relative inline-flex h-9 items-center gap-2 rounded-[var(--radius-control)] border border-rule bg-transparent px-3 font-sans text-ui font-medium transition-colors after:absolute after:-inset-[5px] after:content-[''] hover:bg-surface-2/55 aria-pressed:bg-surface-2/55",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
          "max-sm:size-11 max-sm:justify-center max-sm:gap-0 max-sm:border-transparent max-sm:bg-transparent max-sm:px-0 max-sm:hover:bg-surface-2",
          down ? "text-ink-2" : "text-ink",
        )}
      >
        <LampMark
          look={lampLook(read.mood, sheetOpen)}
          className={cn(
            down ? "text-ink-3" : "text-accent-text",
            "max-sm:size-[var(--icon-md)]",
          )}
        />
        <span aria-hidden="true" className="max-sm:hidden">
          {down ? `${DJINN_NAME} · ${downWord}` : DJINN_NAME}
        </span>
        {count > 0 && !sheetOpen ? (
          <span role="img" aria-label="Something waits for you" className="dj-count">
            <span aria-hidden="true">{count}</span>
          </span>
        ) : null}
        <Kbd aria-hidden="true" className="bg-transparent px-0 text-ink-2 max-sm:hidden">
          {DJINN_SHORTCUT}
        </Kbd>
      </button>
      {peeking && newest ? (
        <Suspense fallback={null}>
          <Peek
            id={peekId}
            projectId={projectId}
            replyId={newest.reply_id}
            environment={ledger?.watch.environment}
          />
        </Suspense>
      ) : null}
      {phone && found && !sheetOpen && newest ? (
        <Suspense fallback={null}>
          <PhoneFinding
            projectId={projectId}
            replyId={newest.reply_id}
            title={newest.title}
            onOpen={onOpen}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
